/**
 * Network thermal printers (M7-6; plan §3 "Print spooler", §11 "Printers"). A printer belongs to one
 * location, is reached over the branch network (ESC/POS on TCP, port 9100 by default) and prints on
 * 80 mm or 58 mm paper. Printers are a synced entity: the admin web changes them through the API,
 * and Dob Desktop through sync commands, so a branch can fix a printer's address while its
 * internet is down (the server checks the command when it arrives). "Remove" retires a printer:
 * it disappears from use but stays in history, because old print jobs refer to it.
 */
import { z } from 'zod';

import { parseLanAddress } from '../network/address.ts';

export const PRINTER_ROLES = ['RECEIPT', 'KITCHEN', 'OTHER'] as const;
export type PrinterRole = (typeof PRINTER_ROLES)[number];

/** Paper widths in millimetres; 80 mm unless the owner's printer says otherwise (O28). */
export const PAPER_WIDTHS = [80, 58] as const;
export type PaperWidth = (typeof PAPER_WIDTHS)[number];
export const DEFAULT_PAPER_WIDTH: PaperWidth = 80;

/**
 * Printable dots across the paper at 203 dpi (8 dots per millimetre), the usual thermal head:
 * 72 mm of an 80 mm roll, 48 mm of a 58 mm roll.
 */
export const PRINT_DOTS: Readonly<Record<PaperWidth, number>> = { 80: 576, 58: 384 };

/** ESC/POS "raw" printing port of network printers. */
export const DEFAULT_PRINTER_PORT = 9100;
export const PRINTER_NAME_MAX = 60;
export const PRINTER_RETIRE_REASON_MAX = 500;

/** Sync command types (apps/api/src/printers). */
export const PRINTER_COMMANDS = {
  create: 'printer.create',
  update: 'printer.update',
  retire: 'printer.retire',
} as const;

/** The printer's address as typed (IPv4 or network name; Arabic digits accepted), normalized. */
const hostSchema = z.string().transform((value, ctx) => {
  const parsed = parseLanAddress(value);
  if (!parsed || parsed.port !== null) {
    ctx.addIssue({
      code: 'custom',
      message: 'printer_address',
      params: { dobCode: 'printer_address' },
    });
    return z.NEVER;
  }
  return parsed.host;
});

const nameSchema = z.string().trim().min(1).max(PRINTER_NAME_MAX);
const portSchema = z.number().int().min(1).max(65_535);
const paperSchema = z.literal(PAPER_WIDTHS);
const roleSchema = z.enum(PRINTER_ROLES);

/** A new printer (admin web form, and the `printer.create` command without its id). */
export const PrinterFieldsSchema = z.strictObject({
  name: nameSchema,
  host: hostSchema,
  port: portSchema.default(DEFAULT_PRINTER_PORT),
  paperWidthMm: paperSchema.default(DEFAULT_PAPER_WIDTH),
  role: roleSchema.default('RECEIPT'),
});
export type PrinterFields = z.infer<typeof PrinterFieldsSchema>;
export type PrinterFieldsInput = z.input<typeof PrinterFieldsSchema>;

/** Changes to a printer: only the fields given change (at least one). */
export const PrinterPatchSchema = z
  .strictObject({
    name: nameSchema.optional(),
    host: hostSchema.optional(),
    port: portSchema.optional(),
    paperWidthMm: paperSchema.optional(),
    role: roleSchema.optional(),
  })
  .refine((p) => Object.values(p).some((v) => v !== undefined), {
    message: 'Nothing to change',
  });
export type PrinterPatch = z.infer<typeof PrinterPatchSchema>;

export const PrinterRetireSchema = z.strictObject({
  reason: z.string().trim().min(5).max(PRINTER_RETIRE_REASON_MAX),
});

/** Command payloads; the printer id of a new printer is made by the device (UUIDv7). */
export const PrinterCreatePayloadSchema = PrinterFieldsSchema.extend({ printerId: z.uuid() });
export const PrinterUpdatePayloadSchema = z
  .strictObject({
    printerId: z.uuid(),
    name: nameSchema.optional(),
    host: hostSchema.optional(),
    port: portSchema.optional(),
    paperWidthMm: paperSchema.optional(),
    role: roleSchema.optional(),
  })
  .refine((p) => [p.name, p.host, p.port, p.paperWidthMm, p.role].some((v) => v !== undefined), {
    message: 'Nothing to change',
  });
export const PrinterRetirePayloadSchema = PrinterRetireSchema.extend({ printerId: z.uuid() });
export type PrinterCreatePayload = z.infer<typeof PrinterCreatePayloadSchema>;
export type PrinterUpdatePayload = z.infer<typeof PrinterUpdatePayloadSchema>;
export type PrinterRetirePayload = z.infer<typeof PrinterRetirePayloadSchema>;

/** A printer as the screens and the spooler use it. */
export interface Printer {
  readonly id: string;
  readonly locationId: string;
  readonly name: string;
  readonly host: string;
  readonly port: number;
  readonly paperWidthMm: PaperWidth;
  readonly role: PrinterRole;
  readonly retiredAt: string | null;
}

/** A printer on a device: the server's copy, with this site's changes not confirmed yet. */
export interface PrinterView extends Printer {
  /** Changed on this site; the server has not confirmed it yet (it may still refuse it). */
  readonly pending: boolean;
}

function isPaper(value: unknown): value is PaperWidth {
  return value === 80 || value === 58;
}

function isRole(value: unknown): value is PrinterRole {
  return typeof value === 'string' && (PRINTER_ROLES as readonly string[]).includes(value);
}

/** A `printer` row of the sync feed (0015: the table's columns) → Printer; null if malformed. */
export function printerFromRow(row: Record<string, unknown>): Printer | null {
  const { id, location_id, name, host, port, paper_width_mm, role, retired_at } = row;
  if (
    typeof id !== 'string' ||
    typeof location_id !== 'string' ||
    typeof name !== 'string' ||
    typeof host !== 'string' ||
    typeof port !== 'number' ||
    !isPaper(paper_width_mm) ||
    !isRole(role)
  ) {
    return null;
  }
  return {
    id,
    locationId: location_id,
    name,
    host,
    port,
    paperWidthMm: paper_width_mm,
    role,
    retiredAt: typeof retired_at === 'string' ? retired_at : null,
  };
}

/** A command a device still holds (its own, or one a hub holds for its PCs), oldest first. */
export interface PendingPrinterCommand {
  readonly type: string;
  readonly payload: Record<string, unknown>;
  /** Device time of the command (a retirement's time until the server's arrives). */
  readonly occurredAt: string;
}

/**
 * The printers of a location as a device should use them now: its copy of the server's data, with
 * the printer commands made on this site that the server has not decided yet applied on top, in
 * order (the same rules as the server: last change wins per field, a retired printer stays
 * retired, a new printer's id is never reused). So a branch that fixes a printer's address while
 * the internet is down prints to the new address at once; should the server later refuse the
 * change (a sync issue), the device goes back to the server's word.
 */
export function effectivePrinters(
  rows: readonly Record<string, unknown>[],
  pending: readonly PendingPrinterCommand[],
  locationId: string,
): PrinterView[] {
  const printers = new Map<string, PrinterView>();
  for (const row of rows) {
    const printer = printerFromRow(row);
    if (printer?.locationId === locationId)
      printers.set(printer.id, { ...printer, pending: false });
  }
  for (const command of pending) {
    if (command.type === PRINTER_COMMANDS.create) {
      const parsed = PrinterCreatePayloadSchema.safeParse(command.payload);
      if (!parsed.success || printers.has(parsed.data.printerId)) continue;
      const { printerId, ...fields } = parsed.data;
      printers.set(printerId, {
        id: printerId,
        locationId,
        ...fields,
        retiredAt: null,
        pending: true,
      });
    } else if (command.type === PRINTER_COMMANDS.update) {
      const parsed = PrinterUpdatePayloadSchema.safeParse(command.payload);
      const current = parsed.success ? printers.get(parsed.data.printerId) : undefined;
      if (!parsed.success || !current || current.retiredAt !== null) continue;
      const { printerId, ...patch } = parsed.data;
      const changes = Object.fromEntries(
        Object.entries(patch).filter(([, value]) => value !== undefined),
      ) as Partial<Printer>;
      printers.set(printerId, { ...current, ...changes, pending: true });
    } else if (command.type === PRINTER_COMMANDS.retire) {
      const parsed = PrinterRetirePayloadSchema.safeParse(command.payload);
      const current = parsed.success ? printers.get(parsed.data.printerId) : undefined;
      if (!current || current.retiredAt !== null) continue;
      printers.set(current.id, { ...current, retiredAt: command.occurredAt, pending: true });
    }
  }
  return [...printers.values()].sort(
    (a, b) =>
      Number(a.retiredAt !== null) - Number(b.retiredAt !== null) ||
      a.name.localeCompare(b.name, 'ar') ||
      a.id.localeCompare(b.id),
  );
}
